import React, { useState } from 'react';
import {
  Box,
  VStack,
  Text,
  Input,
  Button,
  useToast,
  FormControl,
  FormErrorMessage
} from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { subscribeToNewsletter } from '../utils/newsletterService';

interface NewsletterSignupProps {
  source?: string;
  title?: string;
  description?: string;
  variant?: 'footer' | 'inline';
}

const NewsletterSignup: React.FC<NewsletterSignupProps> = ({ 
  source = 'footer',
  title,
  description,
  variant = 'footer'
}) => {
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [error, setError] = useState('');
  const toast = useToast();
  const { t } = useTranslation('forms');

  // Use props if provided, otherwise fall back to translations
  const displayTitle = title || t('newsletter.title', { defaultValue: 'Stay Updated' });
  const displayDescription = description || t('newsletter.description', { defaultValue: 'Get notified about new print releases and photography stories.' });

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!email.trim()) {
      setError(t('validation.required'));
      return;
    }

    if (!validateEmail(email.trim())) {
      setError(t('validation.email'));
      return;
    }

    setIsLoading(true);

    try {
      const result = await subscribeToNewsletter(email.trim(), source);
      
      if (result.success) {
        setIsSubscribed(true);
        setEmail('');
        toast({
          title: "Newsletter Subscription",
          description: result.message,
          status: "success",
          duration: 5000,
          isClosable: true,
        });
      } else {
        setError(result.message);
        toast({
          title: "Subscription Failed", 
          description: result.message,
          status: "warning",
          duration: 5000,
          isClosable: true,
        });
      }
    } catch (error) {
      setError('Something went wrong. Please try again.');
      toast({
        title: "Error",
        description: "Failed to subscribe. Please try again.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }

    setIsLoading(false);
  };

  if (isSubscribed && variant === 'footer') {
    return (
      <Box textAlign="center" py={4}>
        <Text 
          fontSize="sm" 
          color="white" 
          fontFamily="Source Code Pro"
        >
          ✅ {t('newsletter.success')}
        </Text>
      </Box>
    );
  }

  if (variant === 'inline') {
    return (
      <Box 
        border="2px solid black" 
        p={6} 
        bg="gray.50"
        maxW="400px"
        mx="auto"
      >
        <VStack spacing={4} align="stretch">
          <VStack spacing={2} align="start">
            <Text 
              fontSize="lg" 
              fontWeight="bold" 
              fontFamily="Abel"
              color="black"
            >
              {displayTitle}
            </Text>
            <Text 
              fontSize="sm" 
              fontFamily="Source Code Pro"
              color="black"
            >
              {displayDescription}
            </Text>
          </VStack>

          <form onSubmit={handleSubmit}>
            <VStack spacing={3}>
              <FormControl isInvalid={!!error}>
                <Input
                  type="email"
                  placeholder={t('newsletter.placeholder')}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  fontFamily="Source Code Pro"
                  border="2px solid black"
                  _focus={{ borderColor: "black", boxShadow: "none" }}
                  bg="white"
                />
                <FormErrorMessage fontFamily="Source Code Pro">
                  {error}
                </FormErrorMessage>
              </FormControl>

              <Button
                type="submit"
                bg="black"
                color="white"
                fontFamily="Source Code Pro"
                _hover={{ bg: "gray.800" }}
                isLoading={isLoading}
                loadingText={t('newsletter.subscribing')}
                w="full"
              >
                {t('newsletter.subscribe')}
              </Button>
            </VStack>
          </form>

          <Text 
            fontSize="xs" 
            color="gray.600" 
            textAlign="center"
            fontFamily="Source Code Pro"
          >
            {t('newsletter.disclaimer')}
          </Text>
        </VStack>
      </Box>
    );
  }

  // Footer variant
  return (
    <VStack spacing={3} align="stretch" w="100%">
      <Text 
        fontSize="sm" 
        fontWeight="bold" 
        color="white" 
        fontFamily="Abel"
      >
        {displayTitle}
      </Text>
      <Text 
        fontSize="xs" 
        color="gray.300" 
        fontFamily="Source Code Pro"
      >
        {displayDescription}
      </Text>
      
      <form onSubmit={handleSubmit}>
        <VStack spacing={2}>
          <FormControl isInvalid={!!error}>
            <Input
              type="email"
              placeholder={t('newsletter.placeholder')}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              fontFamily="Source Code Pro"
              bg="white"
              color="black"
              border="1px solid gray"
              _focus={{ borderColor: "white", boxShadow: "0 0 0 1px white" }}
              size="sm"
            />
            {error && (
              <FormErrorMessage fontSize="xs" fontFamily="Source Code Pro">
                {error}
              </FormErrorMessage>
            )}
          </FormControl>

          <Button
            type="submit"
            bg="white"
            color="black"
            fontFamily="Source Code Pro"
            _hover={{ bg: "gray.100" }}
            isLoading={isLoading}
            loadingText="..."
            size="sm"
            w="full"
          >
            {t('newsletter.subscribe')}
          </Button>
        </VStack>
      </form>

      <Text 
        fontSize="xs" 
        color="gray.400" 
        textAlign="center"
        fontFamily="Source Code Pro"
      >
        {t('newsletter.disclaimer')}
      </Text>
    </VStack>
  );
};

export default NewsletterSignup;